import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Download,
  ListChecks,
  ArrowRight,
  Plus,
  Users,
  FolderOpen,
  Settings2,
  CheckCircle,
  User,
  Briefcase
} from 'lucide-react';
import { useSettings } from '../contexts/SettingsContext';
import { useNavigate } from 'react-router-dom';
import { CardDrawer } from './CRM';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const ETAPAS_CLIENTE = [
  'PAGAMENTO_CONFIRMADO',
  'AGUARDANDO_DOCUMENTOS',
  'DOCUMENTACAO_PENDENTE',
  'DOCUMENTACAO_RECEBIDA',
  'EM_EXECUCAO',
  'EM_PRODUCAO',
  'PRODUCAO',
  'ANALISE_EM_ANDAMENTO',
  'FINALIZADOS',
  'FINALIZADO',
  'CONCLUIDO',
  'POS_ACOMPANHAMENTO'
];

const clienteElegivel = lead => {
  const etapa = String(
    lead.stage || ''
  ).toUpperCase();

  const pagamento = String(
    lead.pagamento_status || ''
  ).toLowerCase();

  return (
    ETAPAS_CLIENTE.includes(etapa) ||
    pagamento === 'approved'
  );
};

const Clients = () => {
  const { darkMode } = useSettings();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [checklists, setChecklists] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState(null);
  const [showProductionLine, setShowProductionLine] = useState(false);

  useEffect(() => {
    fetchClients();
  }, []);


  const calculateProgress = (checklist) => {
    if (!checklist) return 0;
    const items = [
      'atualizacao_cadastral', 'atualizacao_endereco', 'atualizacao_dados_pessoais',
      'vida_financeira', 'verificar_email', 'consulta_seguro', 'antes', 'cadastro', 'depois'
    ];
    const completed = items.filter(item => checklist[item] === true || Number(checklist[item]) === 1).length;
    return Math.round((completed / items.length) * 100);
  };

  const isSLABreached = (client, checklist) => {
    if (!client || !client.data_criacao) return false;
    
    const createdAt = new Date(client.data_criacao);
    const now = new Date();
    const daysSinceCreation = (now - createdAt) / (1000 * 60 * 60 * 24);
    
    // Check if more than 3 days have passed
    if (daysSinceCreation > 3) {
      // Check if no checklist items have been started
      if (!checklist) return true;
      
      const items = [
        'atualizacao_cadastral', 'atualizacao_endereco', 'atualizacao_dados_pessoais',
        'vida_financeira', 'verificar_email', 'consulta_seguro', 'antes', 'cadastro', 'depois'
      ];
      const hasStartedItems = items.some(item => checklist[item] === 1);
      
      return !hasStartedItems;
    }
    
    return false;
  };

  // Agrupar clientes por parceiro
  const groupClientsByPartner = () => {
    const grouped = {};
    clients.forEach(client => {
      const partner = client.parceiro || 'Direto';
      if (!grouped[partner]) {
        grouped[partner] = [];
      }
      grouped[partner].push(client);
    });
    return grouped;
  };

  const groupedClients = groupClientsByPartner();
  const partners = Object.keys(groupedClients);

  const fetchClients = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/leads`
      );

        const payload = response.data?.data ?? response.data;

        const todos = Array.isArray(payload) ? payload : [];
        const elegiveis = todos.filter(clienteElegivel);

        setClients(elegiveis);

        const checklistMap = {};
        for (const cliente of elegiveis) {
          checklistMap[cliente.id] =
            cliente.checklist_status &&
            typeof cliente.checklist_status === "object"
              ? cliente.checklist_status
              : {};
        }
        setChecklists(checklistMap);
    } catch (error) {
      console.error(
        'Erro ao buscar clientes:',
        error
      );

      setClients([]);
    } finally {
      setLoading(false);
    }
  };

  const exportToSheet = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/leads/export/sheet`,
        {
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `clientes_${Date.now()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Erro ao exportar:', error);
    }
  };

  const exportSimplified = async () => {
    try {
      const response = await axios.get(`${API_URL}/leads/export/simplified`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `clientes_simplificado_${Date.now()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Erro ao exportar simplificado:', error);
    }
  };

  const etapaCliente = client =>
    String(client.stage || '').toUpperCase();

  const totalDocumentacao = clients.filter(
    client => client.documentacao_completa === true
  ).length;

  const totalProducao = clients.filter(
    client => [
      'EM_EXECUCAO',
      'EM_PRODUCAO',
      'PRODUCAO',
      'ANALISE_EM_ANDAMENTO'
    ].includes(etapaCliente(client))
  ).length;

  const totalFinalizados = clients.filter(
    client => [
      'FINALIZADOS',
      'FINALIZADO',
      'CONCLUIDO',
      'POS_ACOMPANHAMENTO'
    ].includes(etapaCliente(client))
  ).length;

  const totalPF = clients.filter(
    client => client.tipo_pessoa === 'PF'
  ).length;

  const totalPJ = clients.filter(
    client => client.tipo_pessoa === 'PJ'
  ).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-gray-500">Carregando...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className={`text-3xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Clientes Rating</h1>
          <p className={darkMode ? 'text-gray-400' : 'text-gray-600'}>Gerencie o checklist de serviços de cada cliente</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => navigate('/clients/add')}
            className="flex items-center gap-2 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors"
          >
            <Plus size={20} />
            Adicionar Cliente
          </button>
          <button
            onClick={exportSimplified}
            className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Download size={20} />
            Download Simplificado
          </button>
          <button
            onClick={exportToSheet}
            className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
          >
            <Download size={20} />
            Exportar Completo
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-6 gap-4">
        {[
          {
            label: 'Total de Clientes',
            value: clients.length,
            icon: Users,
            iconClass: 'bg-blue-600'
          },
          {
            label: 'Documentação concluída',
            value: totalDocumentacao,
            icon: FolderOpen,
            iconClass: 'bg-orange-500'
          },
          {
            label: 'Em Produção',
            value: totalProducao,
            icon: Settings2,
            iconClass: 'bg-cyan-600'
          },
          {
            label: 'Finalizados',
            value: totalFinalizados,
            icon: CheckCircle,
            iconClass: 'bg-green-600'
          },
          {
            label: 'Pessoa Física',
            value: totalPF,
            icon: User,
            iconClass: 'bg-indigo-600'
          },
          {
            label: 'Pessoa Jurídica',
            value: totalPJ,
            icon: Briefcase,
            iconClass: 'bg-purple-600'
          }
        ].map((stat, index) => {
          const Icon = stat.icon;

          return (
            <div
              key={index}
              className={
                `${
                  darkMode
                    ? 'bg-gray-800 border-gray-700'
                    : 'bg-white border-gray-200'
                } border rounded-xl p-4 shadow-sm min-h-[142px]`
              }
            >
              <div className="h-full flex flex-col">
                <div className={
                  `${stat.iconClass} w-11 h-11 rounded-lg text-white flex items-center justify-center`
                }>
                  <Icon size={22} />
                </div>

                <p className={
                  `${
                    darkMode
                      ? 'text-white'
                      : 'text-gray-900'
                  } text-2xl font-bold mt-3 leading-none`
                }>
                  {stat.value}
                </p>

                <p className={
                  `${
                    darkMode
                      ? 'text-gray-300'
                      : 'text-gray-600'
                  } text-sm leading-5 mt-2`
                }>
                  {stat.label}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Clients grouped by Partner */}
      {partners.map(partner => {
        const partnerClients = groupedClients[partner];
        const inProgress = partnerClients.filter(c => calculateProgress(checklists[c.id]) < 100);
        const completed = partnerClients.filter(c => calculateProgress(checklists[c.id]) === 100);

        return (
          <div key={partner} className="space-y-4">
            {/* Partner Header */}
            <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-sm p-4`}>
              <h2 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                🏢 {partner} ({partnerClients.length} clientes)
              </h2>
            </div>

            {/* Em Andamento */}
            {inProgress.length > 0 && (
              <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-sm overflow-hidden`}>
                <div className={
                  `${
                    darkMode
                      ? 'bg-gray-800 border-gray-700'
                      : 'bg-gray-100 border-gray-200'
                  } p-4 border-b`
                }>
                  <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    🔄 Em Andamento ({inProgress.length})
                  </h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className={darkMode ? 'bg-gray-700' : 'bg-gray-50'}>
                      <tr className={`text-left text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        <th className="px-6 py-3">Nome</th>
                        <th className="px-6 py-3">CPF/CNPJ</th>
                        <th className="px-6 py-3">Tipo</th>
                        <th className="px-6 py-3">Progresso Rating</th>
                        <th className="px-6 py-3">Etapa Atual</th>
                        <th className="px-6 py-3">Entrada</th>
                        <th className="px-6 py-3">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {inProgress.map((client) => {
                        const checklist = checklists[client.id];
                        const progress = calculateProgress(checklist);
                        return (
                          <tr
                            key={client.id}
                            className={
                              `${
                                darkMode
                                  ? 'border-gray-700 hover:bg-gray-700'
                                  : 'border-gray-200 hover:bg-gray-50'
                              } border-t`
                            }
                          >
                            <td className={`px-6 py-4 font-medium ${darkMode ? 'text-white' : ''}`}>{client.nome}</td>
                            <td className={`px-6 py-4 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{client.cpf_cnpj}</td>
                            <td className={`px-6 py-4 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                              <span className={`px-2 py-1 rounded text-xs font-medium ${
                                client.tipo_pessoa === 'PF' 
                                  ? 'bg-blue-100 text-blue-800' 
                                  : 'bg-purple-100 text-purple-800'
                              }`}>
                                {client.tipo_pessoa === 'PF' ? 'PF' : 'PJ'}
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-3">
                                <div className="w-24 bg-gray-200 rounded-full h-2">
                                  <div
                                    className="h-2 rounded-full transition-all bg-blue-600"
                                    style={{ width: `${progress}%` }}
                                  />
                                </div>
                                <span className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                  {progress}%
                                </span>
                              </div>
                            </td>
                            <td className={`px-6 py-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                              <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                {String(
                                  client.stage || '—'
                                ).replace(/_/g, ' ')}
                              </span>
                            </td>
                            <td className={`px-6 py-4 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                              {client.data_assinatura ||
                              client.created_at ||
                              client.data_criacao
                                ? new Date(
                                    client.data_assinatura ||
                                    client.created_at ||
                                    client.data_criacao
                                  ).toLocaleDateString('pt-BR')
                                : '—'}
                            </td>
                            <td className="px-6 py-4">
                              <button
                                onClick={() => {
                                  setSelectedLead(client);
                                  setShowProductionLine(true);
                                }}
                                className="flex items-center gap-2 text-blue-600 hover:text-blue-800 text-sm font-medium"
                              >
                                <ListChecks size={16} />
                                Esteira
                                <ArrowRight size={16} />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Serviço Completo */}
            {completed.length > 0 && (
              <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-sm overflow-hidden`}>
                <div className={
                  `${
                    darkMode
                      ? 'bg-gray-800 border-gray-700'
                      : 'bg-gray-100 border-gray-200'
                  } p-4 border-b`
                }>
                  <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    ✅ Serviço Completo ({completed.length})
                  </h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className={darkMode ? 'bg-gray-700' : 'bg-gray-50'}>
                      <tr className={`text-left text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        <th className="px-6 py-3">Nome</th>
                        <th className="px-6 py-3">CPF/CNPJ</th>
                        <th className="px-6 py-3">Tipo</th>
                        <th className="px-6 py-3">Progresso Rating</th>
                        <th className="px-6 py-3">Etapa Atual</th>
                        <th className="px-6 py-3">Entrada</th>
                        <th className="px-6 py-3">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {completed.map((client) => {
                        const checklist = checklists[client.id];
                        const progress = calculateProgress(checklist);
                        return (
                          <tr key={client.id} className={`border-t ${darkMode ? 'border-gray-700 hover:bg-gray-700' : 'hover:bg-gray-50'}`}>
                            <td className={`px-6 py-4 font-medium ${darkMode ? 'text-white' : ''}`}>{client.nome}</td>
                            <td className={`px-6 py-4 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{client.cpf_cnpj}</td>
                            <td className={`px-6 py-4 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                              <span className={`px-2 py-1 rounded text-xs font-medium ${
                                client.tipo_pessoa === 'PF' 
                                  ? 'bg-blue-100 text-blue-800' 
                                  : 'bg-purple-100 text-purple-800'
                              }`}>
                                {client.tipo_pessoa === 'PF' ? 'PF' : 'PJ'}
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-3">
                                <div className="w-24 bg-gray-200 rounded-full h-2">
                                  <div
                                    className="h-2 rounded-full transition-all bg-green-600"
                                    style={{ width: `${progress}%` }}
                                  />
                                </div>
                                <span className={`text-sm font-medium text-green-600`}>
                                  {progress}%
                                </span>
                              </div>
                            </td>
                            <td className={`px-6 py-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                              <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                {String(
                                  client.stage || '—'
                                ).replace(/_/g, ' ')}
                              </span>
                            </td>
                            <td className={`px-6 py-4 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                              {client.data_assinatura ||
                              client.created_at ||
                              client.data_criacao
                                ? new Date(
                                    client.data_assinatura ||
                                    client.created_at ||
                                    client.data_criacao
                                  ).toLocaleDateString('pt-BR')
                                : '—'}
                            </td>
                            <td className="px-6 py-4">
                              <button
                                onClick={() => {
                                  setSelectedLead(client);
                                  setShowProductionLine(true);
                                }}
                                className="flex items-center gap-2 text-blue-600 hover:text-blue-800 text-sm font-medium"
                              >
                                <ListChecks size={16} />
                                Esteira
                                <ArrowRight size={16} />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        );
      })}

      {clients.length === 0 && (
        <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-sm p-12 text-center ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          Nenhum cliente encontrado
        </div>
      )}

      {/* Drawer oficial da Esteira de Produção */}
      {showProductionLine && selectedLead && (
        <CardDrawer
          lead={selectedLead}
          coluna={
            selectedLead.status_kanban ||
            'EM_PRODUCAO'
          }
          onClose={() => {
            setShowProductionLine(false);
            setSelectedLead(null);
            fetchClients();
          }}
          onColumnChange={() => {
            fetchClients();
          }}
          onReset={() => {}}
          onDelete={() => {}}
        />
      )}
    </div>
  );
};

export default Clients;
